// quiz-rituals.js — Named rituals mapped from quiz answers.
//
// The customer's answers route them to a named ritual ("The Anchoress",
// "The Apothecary", etc.) so their recommendation feels like a specific,
// chosen thing rather than a generic "your daily ritual."
//
// Schema:
//   {
//     name:        string  — display name shown prominently on the recommendation page
//     tagline:     string  — one-line italic subtitle (punchy first sentence)
//     description: string  — longer prose elaboration ("horoscope-style" — addresses
//                            the customer, references their answers, evokes the ritual)
//     matches:     string[] — answer keys this ritual fits (format: "questionId.answerId").
//                            More overlaps with the customer's answers = better match.
//                            Multi-select answers each contribute one overlap per picked
//                            option that's in the list.
//   }
//
// Picking logic: pickRitual(answers) walks HH_RITUALS in order, counts how many
// of each ritual's match keys appear in the customer's answers, and returns
// the highest-scoring one. Ties broken by array order (first wins). If no
// ritual matches anything, returns DEFAULT_RITUAL.
//
// The 8 archetypes below are anchored in HH's monastery-feminine vocabulary
// (anchoress, abbess, apothecary, novice, etc.). The tagline + description
// follow Mary's "more like a horoscope" instruction: address the customer
// directly in second person, reference the actives in their bundle obliquely,
// and let the medieval vocation hold the whole thing together.

const HH_RITUALS = [
  {
    name: "The Anchoress",
    tagline: "For the sensitive heart and the sensitive face.",
    description: "You came here needing softness. The Anchoress is the gentlest of the rituals — calendula and Roman chamomile for skin that flushes easily, lavender and frankincense to slow the nervous system, and the scents of the chapel: resins, soft herbs, the smoke of beeswax. It is a ritual for the woman who keeps her own counsel.",
    matches: [
      "top_concern.sensitivity",
      "top_concern.eczema",
      "dermatologist.eczema",
      "wellness.anxiety",
      "wellness.sleep",
      "scent.resins_incense",
      "scent.soft_herbs",
    ],
  },
  {
    name: "The Pilgrim",
    tagline: "For weather, for travel, for the long road.",
    description: "Your skin tells the story of your days — sun, wind, work, the constant outdoors. The Pilgrim ritual is built around what mends what weather wears down: tallow rendered with calendula, sea buckthorn and St. John's wort for what has been most exposed, and roll-ons for stiffness and sinus pressure. It walks where you walk.",
    matches: [
      "top_concern.weathered",
      "top_concern.lips_hands",
      "top_concern.dryness",
      "wellness.joint_pain",
      "wellness.respiratory",
      "skin_type.dry",
    ],
  },
  {
    name: "The Abbess",
    tagline: "For the woman who holds many things at once.",
    description: "You've cared for your skin for years, and lately it doesn't respond the way it once did. The Abbess ritual is for the seasoned face — frankincense and myrrh for the slow work of regeneration, calendula for the small repairs no one notices, and clary sage for the hormonal weather of a deeper season. You're not starting over. You're going deeper.",
    matches: [
      "top_concern.aging",
      "top_concern.dark_spots",
      "wellness.hormonal",
      "wellness.focus",
      "wellness.grief",
      "scent.warm_woods",
      "scent.powdery_florals",
    ],
  },
  {
    name: "The Apothecary",
    tagline: "For the hands that tend to everyone else.",
    description: "Your answers pointed toward repair — scars, chapping, redness, the small wounds of being the one who heals. The Apothecary ritual is anchored in calendula, the monastery's most-trusted wound flower, doubled in tallow and salve. Frankincense and Roman chamomile for sensitive patches; nothing harsh. The remedies in your cabinet, refined.",
    matches: [
      "top_concern.scars",
      "top_concern.eczema",
      "top_concern.sensitivity",
      "top_concern.lips_hands",
      "dermatologist.eczema",
      "dermatologist.psoriasis",
    ],
  },
  {
    name: "The Mystic",
    tagline: "For the contemplative life, even in the kitchen.",
    description: "You're drawn to the scents of the sanctuary — frankincense, myrrh, cedar, the woods of the temple. The Mystic ritual leans into the sacred resins: oils that have been used in liturgy since the prophets, paired with botanicals that quiet the mind. It is a ritual for the woman who reads the breviary while the soup simmers.",
    matches: [
      "wellness.anxiety",
      "wellness.grief",
      "wellness.focus",
      "scent.resins_incense",
      "scent.cool_woods",
      "scent.warm_woods",
    ],
  },
  {
    name: "The Gardener",
    tagline: "For green hands and a verdant soul.",
    description: "Your scent preferences gave you away — violet leaf, herbs, the cool green of crushed leaves. The Gardener ritual draws from the cloister garden: clary sage, lavender, rosemary, the herbs the sisters dried in bundles from the rafters. Earthy, herbal, and quietly Irish. A ritual that smells of soil and morning.",
    matches: [
      "scent.green_grassy",
      "scent.soft_herbs",
      "top_concern.hair_scalp",
      "hair_goal.maintain",
      "hair_goal.frizz",
      "wellness.anxiety",
    ],
  },
  {
    name: "The Novice",
    tagline: "For young skin and a beginning.",
    description: "Your skin doesn't need rescuing yet — and you came to this quiz curious rather than concerned. The Novice ritual is anchored in our citrus florals: bergamot, jasmine, neroli — the perfume of a Mediterranean courtyard. Daily care that maintains rather than restores. The first steps of a long practice.",
    matches: [
      "skin_type.balanced",
      "hair_goal.none",
      "top_concern.none",
      "scent.bright_citrus",
      "scent.sweet_citrus",
      "scent.white_florals",
      "scent.surprise_me",
    ],
  },
  {
    name: "The Hearthkeeper",
    tagline: "For the woman whose body is the first line of defense.",
    description: "You're the one everyone runs to when they're getting sick. The Hearthkeeper ritual is built around defense and recovery — Thieves oil for everyday immune support, sinus relief for allergies and household colds, and gentle skincare that holds up under exhaustion. A ritual for those who keep the household well.",
    matches: [
      "wellness.respiratory",
      "wellness.sleep",
      "wellness.anxiety",
      "wellness.joint_pain",
      "top_concern.lips_hands",
      "top_concern.sensitivity",
    ],
  },
];

// Fallback when no ritual's match keys overlap the customer's answers
// (rare — would require very minimal/contradictory quiz input).
const DEFAULT_RITUAL = {
  name: "Your Daily Ritual",
  tagline: null,
  description: null,
};

// =============================================================================
// pickRitual(answers) -> { name, tagline, description }
// Pure function. No DOM. No React.
//
// Question weights: top_concern hits count 2× because the customer's
// stated concerns are the strongest signal of what their ritual should
// be. Wellness, scent, hair etc. each count 1×. Parallel to the scoring
// engine's top_concern weight.
// =============================================================================
const QUESTION_WEIGHTS = { top_concern: 2 };

function pickRitual(answers) {
  const safe = answers || {};

  // Build a flat set of "questionId.answerId" keys from the customer's answers.
  // Multi-select answers contribute one key per picked option.
  const customerKeys = new Set();
  Object.keys(safe).forEach(function (qId) {
    const val = safe[qId];
    if (val === undefined || val === null) return;
    if (Array.isArray(val)) {
      val.forEach(function (aId) { customerKeys.add(qId + "." + aId); });
    } else {
      customerKeys.add(qId + "." + val);
    }
  });

  let best = null;
  let bestScore = 0;
  for (let i = 0; i < HH_RITUALS.length; i++) {
    const ritual = HH_RITUALS[i];
    const matches = ritual.matches || [];
    let score = 0;
    for (let j = 0; j < matches.length; j++) {
      const key = matches[j];
      if (customerKeys.has(key)) {
        const qId = key.split('.')[0];
        score += QUESTION_WEIGHTS[qId] || 1;
      }
    }
    // Strict > so the first ritual in array order wins ties.
    if (score > bestScore) {
      bestScore = score;
      best = ritual;
    }
  }

  if (best && bestScore > 0) {
    return { name: best.name, tagline: best.tagline, description: best.description };
  }
  return DEFAULT_RITUAL;
}

window.HH_RITUALS = HH_RITUALS;
window.HH_DEFAULT_RITUAL = DEFAULT_RITUAL;
window.pickRitual = pickRitual;
